import {addCommas, createPercentageString, parseCurrency, toCurrency} from './functions';

/**
 * Populate a Category array with data
 *
 * @param data
 * @return {*}
 */
export const populateCategoryTable = (data) => {

    const emptyCurrency = toCurrency(0);
    let tableArray = [];

    // Assemble data for each Category row in the requested array
    if (data.aggregations.product_categories_returned && data.aggregations.product_categories_returned.by_category.buckets) {
        let crResults = data.aggregations.product_categories_returned.by_category.buckets;
        let crTotal = crResults.length;
        if (crTotal) {
            for (let cr = 0; cr < crTotal; cr++) {
                let crResult = crResults[cr];
                let category = {
                    id: crResult.key,
                    name: crResult.column_name.hits.hits[0]._source.product_category_name,
                    qty_returned: (crResult.category_qty && crResult.category_qty.value) ? addCommas(crResult.category_qty.value) : 0,
                    subtotal_returned: (crResult.refund_subtotal && crResult.refund_subtotal.value) ? toCurrency(crResult.refund_subtotal.value, true) : emptyCurrency
                };

                // Create a Category row and add it to the table
                let categoryRow = createTableRow('category', category);
                tableArray.push(categoryRow);
            }
        }
    }
    if (data.aggregations.product_categories_sold && data.aggregations.product_categories_sold.by_category.buckets) {
        let csResults = data.aggregations.product_categories_sold.by_category.buckets;
        let csTotal = csResults.length;
        if (csTotal) {
            for (let cs = 0; cs < csTotal; cs++) {
                let csResult = csResults[cs];
                let category = {
                    id: csResult.key,
                    name: csResult.column_name.hits.hits[0]._source.product_category_name,
                    qty_sold: (csResult.category_qty && csResult.category_qty.value) ? addCommas(csResult.category_qty.value) : 0,
                    subtotal_sold: (csResult.payment_subtotal && csResult.payment_subtotal.value) ? toCurrency(csResult.payment_subtotal.value, true) : emptyCurrency
                };

                // Determine if this Category is already in the table
                const cIdx = tableArray.findIndex(c => c.category_id.toString() === category.id.toString());
                if (cIdx === -1) {
                    // Add this Category to the table
                    let categoryRow = createTableRow('category', category);
                    tableArray.push(categoryRow);
                } else {
                    // Update the existing Category in the table
                    tableArray[cIdx].qty_sold = addCommas(category.qty_sold);
                    tableArray[cIdx].subtotal_sold = addCommas(category.subtotal_sold);
                }
            }
        }
    }
    // Update the Category table with qty net totals
    let qtyNetTotal = 0;
    for (let cn = 0; cn < tableArray.length; cn++) {
        let cnResult = tableArray[cn];
        let qtyReturned = parseCurrency(cnResult.qty_returned);
        let qtySold = parseCurrency(cnResult.qty_sold);
        let subtotalReturned = parseCurrency(cnResult.subtotal_returned);
        let subtotalSold = parseCurrency(cnResult.subtotal_sold);
        tableArray[cn].qty_net = addCommas(qtySold - qtyReturned);
        tableArray[cn].net_total = toCurrency(subtotalSold - subtotalReturned);
        qtyNetTotal += parseCurrency(cnResult.qty_net);
    }

    // Update the Category table with percentages
    for (let cp = 0; cp < tableArray.length; cp++) {
        let cpResult = tableArray[cp];
        let percent = ((parseCurrency(cpResult.qty_net) / qtyNetTotal) * 100);
        if (percent < 0) {
            percent = 0;
        }
        tableArray[cp].percent = createPercentageString(percent);
    }

    return tableArray;
};

/**
 *Populate a Discount array with data
 *
 * @param data
 */
export const populateDiscountListTable = (data) => {

    const emptyCurrency = toCurrency(0);
    let tableArray = [];

    // Assemble filtered data for Area 1: Discounts List
    if (data.aggregations.discount_summary && data.aggregations.discount_summary.by_discount_id.buckets) {
        let lResults = data.aggregations.discount_summary.by_discount_id.buckets;
        let lTotal = lResults.length;
        if (lTotal) {
            for (let i = 0; i < lTotal; i++) {
                let lResult = lResults[i];
                let discount = lResult.column_name.hits.hits[0]._source;
                let totalAmountSaved = emptyCurrency;
                let avgAmountSaved = emptyCurrency;
                let maxAmountSaved = emptyCurrency;
                let minAmountSaved = emptyCurrency;
                let discountQtyRedeemed = 0;

                if (lResult.discount_stats && lResult.discount_stats.sum) {
                    totalAmountSaved = lResult.discount_stats.sum;
                }
                if (lResult.discount_stats && lResult.discount_stats.avg) {
                    avgAmountSaved = lResult.discount_stats.avg;
                }
                if (lResult.discount_stats && lResult.discount_stats.min) {
                    minAmountSaved = lResult.discount_stats.min;
                }
                if (lResult.discount_stats && lResult.discount_stats.max) {
                    maxAmountSaved = lResult.discount_stats.max;
                }
                if (lResult.discount_stats && lResult.discount_stats.count) {
                    discountQtyRedeemed = lResult.discount_stats.count;
                }

                // Create a Location row
                let discountRow = createTableRow('discount', discount);
                discountRow.discount_id = discount.code;
                discountRow.total_amount_saved = (totalAmountSaved) ? toCurrency(totalAmountSaved, true) : emptyCurrency;
                discountRow.avg_amount_saved = (avgAmountSaved) ? toCurrency(avgAmountSaved, true) : emptyCurrency;
                discountRow.max_amount_saved = (maxAmountSaved) ? toCurrency(maxAmountSaved, true) : emptyCurrency;
                discountRow.min_amount_saved = (minAmountSaved) ? toCurrency(maxAmountSaved, true) : emptyCurrency;
                discountRow.discount_qty_redeemed = (discountQtyRedeemed) ? discountQtyRedeemed : 0;

                discountRow.discount_product_subtotal = emptyCurrency;
                discountRow.discount_product_tax = emptyCurrency;
                discountRow.discount_product_total = emptyCurrency;
                discountRow.discount_product_qty = 0;

                // Add the row to the table
                tableArray.push(discountRow);
            }
        }
    }

    if (data.aggregations.product_discount_array_summary && data.aggregations.product_discount_array_summary.discount_array.by_discount.buckets) {
        let lResults = data.aggregations.product_discount_array_summary.discount_array.by_discount.buckets;
        let lTotal = lResults.length;
        if (lTotal) {
            for (let i = 0; i < lTotal; i++) {
                let lResult = lResults[i];
                let productTax = lResult.discounted_products.product_totals.discount_product_tax.value;
                let additionalTax = lResult.discounted_products.product_totals.discount_product_additional_tax.value;
                let totalTax = productTax + additionalTax;

                let discountStatistics = {
                    id: lResult.key,
                    discount_product_subtotal: lResult.discounted_products.product_totals.discount_product_subtotal.value,
                    discount_product_tax: totalTax,
                    discount_product_total: lResult.discounted_products.product_totals.discount_product_total.value,
                    discount_product_qty: lResult.discounted_products.product_totals.discount_product_qty.value
                };

                // Match data to existing table rows by Discount id
                const dIdx = tableArray.findIndex(c => c.discount_id.toString() === discountStatistics.id.toString());
                if (dIdx !== -1) {
                    tableArray[dIdx].discount_product_subtotal = toCurrency(discountStatistics.discount_product_subtotal, true);
                    tableArray[dIdx].discount_product_tax = toCurrency(discountStatistics.discount_product_tax, true);
                    tableArray[dIdx].discount_product_total = toCurrency(discountStatistics.discount_product_total, true);
                    tableArray[dIdx].discount_product_qty = addCommas(discountStatistics.discount_product_qty);
                }
            }
        }
    }

    return tableArray;
};

/**
 * Populate a Product array with data
 *
 * @param data
 */
export const populateProductListTable = (data) => {
    const emptyCurrency = toCurrency(0);
    let tableArray = [];
    if (data.aggregations.products && data.aggregations.products.by_product_unit.buckets) {
        let lResults = data.aggregations.products.by_product_unit.buckets;
        let lTotal = lResults.length;
        if (lTotal) {
            for (let i = 0; i < lTotal; i++) {
                let lResult = lResults[i];
                let product = lResult.column_name.hits.hits[0]._source;
                let totalAmountSavedByDiscount = emptyCurrency;
                let productQty = 0;

                if (lResult.product_discount_total && lResult.product_discount_total.value) {
                    totalAmountSavedByDiscount = lResult.product_discount_total.value;
                }
                if (lResult.discounted_products && lResult.discounted_products.qty_statistics) {
                    productQty = lResult.discounted_products.qty_statistics.sum;
                }

                // Create a Location row
                let productRow = createTableRow('discount_product', product);
                productRow.product_discount_total = (totalAmountSavedByDiscount) ? toCurrency(totalAmountSavedByDiscount, true) : emptyCurrency;
                productRow.total_qty_product_units = (productQty) ? productQty : 0;
                productRow.discounts = [];

                let dResults = lResult.discounts_per_product.buckets;
                let dTotal = dResults.length;
                if (dTotal) {
                    for (let i = 0; i < dTotal; i++) {
                        let dResult = dResults[i];
                        let discountInfo = dResult.column_name.hits.hits[0]._source.discounts;
                        let amountSavedByDiscount = emptyCurrency;
                        let discountQtyRedeemed = 0;
                        let totalQty = 0;

                        if (dResult.discount_total && dResult.discount_total.value) {
                            amountSavedByDiscount = dResult.discount_total.value;
                        }
                        if (dResult.discount_count && dResult.discount_count.value) {
                            discountQtyRedeemed = dResult.discount_count.value;
                        }
                        if (dResult.product_qty_total && dResult.product_qty_total.value) {
                            totalQty = dResult.product_qty_total.value;
                        }

                        // Create a Product Discount row for Dialog
                        let discountRow = createTableRow('product_discount', discountInfo);
                        discountRow.discount_total = (amountSavedByDiscount) ? toCurrency(amountSavedByDiscount, true) : emptyCurrency;
                        discountRow.discount_qty_redeemed = (discountQtyRedeemed) ? discountQtyRedeemed : 0;
                        discountRow.product_qty_total = (totalQty) ? totalQty : 0;

                        // Add the row to the table
                        productRow.discounts.push(discountRow);
                    }
                }

                // Add the row to the table
                tableArray.push(productRow);
            }
        }
    }
    return tableArray;
};

/**
 * Populate a Customer array with data
 *
 * @param data
 * @returns {Array}
 */
export const populateCustomerUsageTable = (data) => {
    const emptyCurrency = toCurrency(0);
    let tableArray = [];

    if (data.aggregations.by_loyalty_account && data.aggregations.by_loyalty_account.buckets) {
        let lResults = data.aggregations.by_loyalty_account.buckets;
        let lTotal = lResults.length;
        if (lTotal) {
            for (let i = 0; i < lTotal; i++) {
                let lResult = lResults[i];
                let customer = lResult.column_name.hits.hits[0]._source.rxtxMsg.payload.loyalty_customer;
                let discountQty = 0;
                let totalAmountSavedByDiscount = emptyCurrency;

                if (lResult.discounts && lResult.discounts.discount_qty) {
                    discountQty = lResult.discounts.discount_qty.value;
                }
                if (lResult.discounts && lResult.discounts.discount_total) {
                    totalAmountSavedByDiscount = lResult.discounts.discount_total.value;
                }

                // Create a Location row
                let customerRow = createTableRow('vip_customer', customer);
                customerRow.discount_qty = (discountQty) ? discountQty : 0;
                customerRow.discount_total = (totalAmountSavedByDiscount) ? toCurrency(totalAmountSavedByDiscount, true) : emptyCurrency;

                // Add the row to the table
                tableArray.push(customerRow);
            }
        }
    }

    return tableArray;
};


/**
 * Populate a Product array with data
 *
 * @param data
 * @return {*}
 */
export const populateProductUnitTable = (data) => {

    const emptyCurrency = toCurrency(0);
    let tableArray = [];

    if (data.aggregations.products_returned && data.aggregations.products_returned.by_product_unit && data.aggregations.products_returned.by_product_unit.buckets) {
        let prResults = data.aggregations.products_returned.by_product_unit.buckets;
        let prTotal = prResults.length;
        if (prTotal) {
            for (let pr = 0; pr < prTotal; pr++) {
                let prResult = prResults[pr];
                let prSrc = prResult.column_name.hits.hits[0]._source;
                let product = {
                    product_brand: (prSrc.product_brand) ? prSrc.product_brand : '(None)',
                    product_category_name: (prSrc.product_category_name) ? prSrc.product_category_name : '(None)',
                    product_name: (prSrc.product_name) ? prSrc.product_name : '(None)',
                    product_type_name: (prSrc.product_type_name) ? prSrc.product_type_name : '(None)',
                    product_unit_id: (prSrc.product_unit_id) ? prSrc.product_unit_id : '(None)',
                    product_unit_type: (prSrc.product_unit_type) ? prSrc.product_unit_type : '(None)'
                };

                // Create a Product row and add it to the table
                let productRow = createTableRow('product', product);
                let taxReturned = (prResult.agg_product_tax_total && prResult.agg_product_tax_total.value) ? prResult.agg_product_tax_total.value : 0;
                let additionalTaxReturned = (prResult.agg_product_tax_total && prResult.agg_product_additional_tax_total.value) ? prResult.agg_product_additional_tax_total.value : 0;
                let totalTax = taxReturned + additionalTaxReturned;
                productRow.qty_returned = (prResult.agg_product_unit_qty && prResult.agg_product_unit_qty.value) ? addCommas(prResult.agg_product_unit_qty.value) : 0;
                productRow.subtotal_returned = (prResult.agg_product_subtotal && prResult.agg_product_subtotal.value) ? toCurrency(prResult.agg_product_subtotal.value, true) : emptyCurrency;
                productRow.tax_returned = (totalTax) ? toCurrency(totalTax, true) : emptyCurrency;
                productRow.total_returned = (prResult.agg_product_total && prResult.agg_product_total.value) ? toCurrency(prResult.agg_product_total.value, true) : emptyCurrency;
                tableArray.push(productRow);
            }
        }
    }
    if (data.aggregations.products_sold && data.aggregations.products_sold.by_product_unit && data.aggregations.products_sold.by_product_unit.buckets) {
        let psResults = data.aggregations.products_sold.by_product_unit.buckets;
        let psTotal = psResults.length;
        if (psTotal) {
            for (let ps = 0; ps < psTotal; ps++) {
                let psResult = psResults[ps];
                let psSrc = psResult.column_name.hits.hits[0]._source;
                let taxSold = (psResult.agg_product_tax_total && psResult.agg_product_tax_total.value) ? psResult.agg_product_tax_total.value : 0;
                let additionalTaxSold = (psResult.agg_product_additional_tax_total && psResult.agg_product_additional_tax_total.value) ? psResult.agg_product_additional_tax_total.value : 0;
                let totalTax = taxSold + additionalTaxSold;

                let product = {
                    product_brand: (psSrc.product_brand) ? psSrc.product_brand : '(None)',
                    product_category_name: (psSrc.product_category_name) ? psSrc.product_category_name : '(None)',
                    product_name: (psSrc.product_name) ? psSrc.product_name : '(None)',
                    product_type_name: (psSrc.product_type_name) ? psSrc.product_type_name : '(None)',
                    product_unit_id: (psSrc.product_unit_id) ? psSrc.product_unit_id : '(None)',
                    product_unit_type: (psSrc.product_unit_type) ? psSrc.product_unit_type : '(None)',
                    qty_sold: (psResult.agg_product_unit_qty && psResult.agg_product_unit_qty.value) ? addCommas(psResult.agg_product_unit_qty.value) : 0,
                    subtotal_sold: (psResult.agg_product_subtotal && psResult.agg_product_subtotal.value) ? toCurrency(psResult.agg_product_subtotal.value, true) : emptyCurrency,
                    tax_sold: (totalTax) ? toCurrency(totalTax, true) : emptyCurrency,
                    total_sold: (psResult.agg_product_total && psResult.agg_product_total.value) ? toCurrency(psResult.agg_product_total.value, true) : emptyCurrency
                };

                // Determine if this Product is already in the table
                const pIdx = tableArray.findIndex(p => p.product_unit_id.toString() === product.product_unit_id.toString());
                if (pIdx === -1) {
                    // Add this Product to the table
                    let productRow = createTableRow('product', product);
                    productRow.qty_sold = product.qty_sold;
                    productRow.subtotal_sold = product.subtotal_sold;
                    productRow.tax_sold = product.tax_sold;
                    productRow.total_sold = product.total_sold;
                    tableArray.push(productRow);
                } else {
                    // Update the existing Product in the table
                    tableArray[pIdx].qty_sold = product.qty_sold;
                    tableArray[pIdx].subtotal_sold = product.subtotal_sold;
                    tableArray[pIdx].tax_sold = product.tax_sold;
                    tableArray[pIdx].total_sold = product.total_sold;
                }
            }
        }
    }

    // Update the Product table with net totals
    for (let pn = 0; pn < tableArray.length; pn++) {
        let pnResult = tableArray[pn];
        let returned = parseCurrency(pnResult.total_returned);
        let sold = parseCurrency(pnResult.total_sold);
        tableArray[pn].net_total = toCurrency(sold - returned);
    }

    return tableArray;
};

/**
 * Generate an object with base data for a table row
 *
 * @param type
 * @param item
 * @return Object
 */
export const createTableRow = (type, item) => {
    if (type === 'location_points') {
        return {
            city: (item.city) ? item.city : '(None)',
            dba_name: (item.dba && item.dba.name) ? item.dba.name : '(None)',
            location_id: (item.id) ? item.id : '-----',
            location_name: (item.name) ? item.name : '(None)',
            pts_spent: 0,
            state: (item.state) ? item.state : '--',
            subtotal_spent: 0,
            subtotal_refunded: 0
        };
    } else if (type === 'location') {
        return {
            city: (item.city) ? item.city : '(None)',
            dba_name: (item.dba && item.dba.name) ? item.dba.name : '(None)',
            district_name: (item.district && item.district.name) ? item.district.name : '(None)',
            location_id: (item.id) ? item.id : '-----',
            location_name: (item.name) ? item.name : '(None)',
            state: (item.state) ? item.state : '--',
            subtotal_sold: '0.00',
            subtotal_returned: '0.00',
            tax_sold: '0.00',
            tax_returned: '0.00',
            total_sold: '0.00',
            total_returned: '0.00',
        };
    } else if (type === 'category') {
        return {
            category_id: (item.id) ? item.id : '----',
            category_name: (item.name) ? item.name : '(None)',
            percent: '0%',
            net_total: (item.net_total) ? item.net_total : '0.00',
            qty_net: 0,
            qty_returned: (item.qty_returned) ? item.qty_returned : 0,
            qty_sold: (item.qty_sold) ? item.qty_sold : 0,
            subtotal_returned: (item.subtotal_returned) ? item.subtotal_returned : '0.00',
            subtotal_sold: (item.subtotal_sold) ? item.subtotal_sold : '0.00'
        };
    } else if (type === 'customer') {
        return {
            active: (item.active) ? 'TRUE' : 'FALSE',
            amount_spent_that_earned_points: '0.00',
            city: (item.city) ? item.city : '(None)',
            customer_id: (item.id) ? item.id : '---------',
            email: (item.email) ? item.email : '(No Email)',
            first_name: (item.first_name) ? item.first_name : '(None)',
            last_name: (item.last_name) ? item.last_name : '(None)',
            num_sales: 0,
            pts_spent: 0,
            state: (item.state) ? item.state : '--',
            telephone: (item.telephone) ? item.telephone : '(None)',
            zip: (item.zip) ? item.zip : '-----'
        };
    } else if (type === 'product') {
        return {
            net_total: '0.00',
            product_brand: (item.product_brand) ? item.product_brand : '(None)',
            product_category_name: (item.product_category_name) ? item.product_category_name : '(None)',
            product_name: (item.product_name) ? item.product_name : '(None)',
            product_type_name: (item.product_type_name) ? item.product_type_name : '(None)',
            product_unit_id: (item.product_unit_id) ? item.product_unit_id : '--------',
            product_unit_type: (item.product_unit_type) ? item.product_unit_type : '(None)',
            qty_sold: 0,
            qty_returned: 0,
            subtotal_sold: '0.00',
            subtotal_returned: '0.00',
            tax_sold: '0.00',
            tax_returned: '0.00',
            total_sold: '0.00',
            total_returned: '0.00',
        };
    } else if (type === 'product_discount') {
        return {
            discount_id: (item.code) ? item.code : '----',
            discount_name: (item.name) ? item.name : '(None)',
            discount_total: '0.00',
            discount_qty_redeemed: 0,
            product_qty_total: 0,
        }
    } else if (type === 'sales_log') {
        return {
            sale_seq: (item.sale_seq) ? item.sale_seq : '------',
            initial_net: (item.initial_net) ? item.initial_net : '0.00',
            initial_tax: (item.initial_tax) ? item.initial_tax : '0.00',
            initial_total: (item.initial_total) ? item.initial_total : '0.00',
            location: (item.location) ? item.location : '----',
            mode: (item.mode) ? item.mode : 'live',
            register: (item.register) ? item.register : '--',
            transaction_date: (item.transaction_date) ? item.transaction_date : 'No Date',
            transaction_date_string: (item.transaction_date_string) ? item.transaction_date_string : 'No Date',
            user: (item.user) ? item.user : '----',
            version: (item.version) ? item.version : 'None'
        };
    } else if (type === 'discount') {
        return {
            discount_id: (item.code) ? item.code : '----',
            discount_name: (item.name) ? item.name : '----',
            total_amount_saved: '0.00',
            avg_amount_saved: '0.00',
            max_amount_saved: '0.00',
            discount_qty_redeemed: 0,
            discount_product_subtotal: '0.00',
            discount_product_tax: '0.00',
            discount_product_total: '0.00',
            discount_product_qty: 0,
        }
    } else if (type === 'discount_location') {
        return {
            location_id: (item.id) ? item.id : '-----',
            location_name: (item.name) ? item.name : '(None)',
            state: (item.state) ? item.state : '(None)',
            city: (item.city) ? item.city : '(None)',
            dba_name: (item.dba && item.dba.name) ? item.dba.name : '(None)',
            discount_total: '0.00',
            discount_qty: 0
        }
    } else if (type === 'discount_product') {
        return {
            product_unit_id: (item.product_unit_id) ? item.product_unit_id : '----',
            product_name: (item.product_name) ? item.product_name : '----',
            product_id: (item.product_id) ? item.product_id : '----',
            product_type_name: (item.product_type_name) ? item.product_type_name : '----',
            product_category_name: (item.product_category_name) ? item.product_category_name : '----',
            product_unit_type: (item.product_unit_type) ? item.product_unit_type : '----',
            product_brand: (item.product_brand) ? item.product_brand : '----',
            product_discount_total: '0.00',
            total_qty_product_units: 0,
        }
    } else if (type === 'vip_customer') {
        return {
            id: (item.id) ? item.id : '----',
            first_name: (item.first_name) ? item.first_name : '(None)',
            last_name: (item.last_name) ? item.last_name : '(None)',
            email: (item.email) ? item.email : '(No Email)',
            telephone: (item.telephone) ? item.telephone : '(None)',
            city: (item.city) ? item.city : '(None)',
            state: (item.state) ? item.state : '(None)',
            zip: (item.zip) ? item.zip : '----',
            registered_by_client: (item.registered_by_client && item.registered_by_client.location) ? item.registered_by_client.location : '----',
            active: (item.active) ? 'TRUE' : 'FALSE',
            discount_qty: 0,
            discount_total: '0.00'
        }
    }
    return {};
};



// WEBPACK FOOTER //
// ./src/Fuse/shared/reportTableFunctions.js